import { baseApi } from "../baseApi";

const storeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Settings ───────────────────────────────────────────────────────────
    getStoreSettings: builder.query({
      query: () => "/api/store/settings",
      providesTags: [{ type: "StoreSettings", id: "SINGLETON" }],
    }),
    updateStoreSettings: builder.mutation({
      query: (body) => ({ url: "/api/store/settings", method: "PUT", body }),
      invalidatesTags: [{ type: "StoreSettings", id: "SINGLETON" }],
    }),

    // ─── Categories ─────────────────────────────────────────────────────────
    getStoreCategories: builder.query({
      query: () => "/api/store/categories",
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "StoreCategory", id })),
              { type: "StoreCategory", id: "LIST" },
            ]
          : [{ type: "StoreCategory", id: "LIST" }],
    }),
    createStoreCategory: builder.mutation({
      query: (body) => ({ url: "/api/store/categories", method: "POST", body }),
      invalidatesTags: [{ type: "StoreCategory", id: "LIST" }],
    }),
    updateStoreCategory: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/store/categories/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StoreCategory", id },
        { type: "StoreCategory", id: "LIST" },
      ],
    }),
    deleteStoreCategory: builder.mutation({
      query: (id) => ({ url: `/api/store/categories/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "StoreCategory", id: "LIST" }],
    }),

    // ─── Products ───────────────────────────────────────────────────────────
    getStoreProducts: builder.query({
      query: (params = {}) => ({ url: "/api/store/products", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "StoreProduct", id })),
              { type: "StoreProduct", id: "LIST" },
            ]
          : [{ type: "StoreProduct", id: "LIST" }],
    }),
    getFeaturedStoreProducts: builder.query({
      query: () => "/api/store/products/featured",
      providesTags: [{ type: "StoreProduct", id: "FEATURED" }],
    }),
    getStoreProductById: builder.query({
      query: (id) => `/api/store/products/${id}`,
      providesTags: (result, error, id) => [{ type: "StoreProduct", id }],
    }),
    createStoreProduct: builder.mutation({
      query: (body) => ({ url: "/api/store/products", method: "POST", body }),
      invalidatesTags: [
        { type: "StoreProduct", id: "LIST" },
        { type: "StoreProduct", id: "FEATURED" },
      ],
    }),
    updateStoreProduct: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/store/products/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StoreProduct", id },
        { type: "StoreProduct", id: "LIST" },
        { type: "StoreProduct", id: "FEATURED" },
      ],
    }),
    deleteStoreProduct: builder.mutation({
      query: (id) => ({ url: `/api/store/products/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "StoreProduct", id: "LIST" },
        { type: "StoreProduct", id: "FEATURED" },
      ],
    }),

    // ─── Inventory ──────────────────────────────────────────────────────────
    getInventoryTransactions: builder.query({
      query: ({ productId, ...params }) => ({
        url: `/api/store/inventory/${productId}/transactions`,
        params,
      }),
      providesTags: (result, error, { productId }) => [
        { type: "StoreInventory", id: productId },
      ],
    }),
    getSizeInventory: builder.query({
      query: (productId) => `/api/store/inventory/${productId}/sizes`,
      providesTags: (result, error, productId) => [
        { type: "StoreSizeInventory", id: productId },
      ],
    }),
    adjustInventory: builder.mutation({
      query: ({ productId, ...body }) => ({
        url: `/api/store/inventory/${productId}/adjust`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: "StoreInventory", id: productId },
        { type: "StoreSizeInventory", id: productId },
        { type: "StoreProduct", id: productId },
        { type: "StoreProduct", id: "LIST" },
      ],
    }),

    // ─── Orders ─────────────────────────────────────────────────────────────
    placeOrder: builder.mutation({
      query: (body) => ({ url: "/api/store/orders", method: "POST", body }),
      invalidatesTags: [
        { type: "StoreOrder", id: "LIST" },
        { type: "StoreOrder", id: "MY_LIST" },
        { type: "StoreProduct", id: "LIST" },
      ],
    }),
    getStoreOrders: builder.query({
      query: (params = {}) => ({ url: "/api/store/orders", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "StoreOrder", id })),
              { type: "StoreOrder", id: "LIST" },
            ]
          : [{ type: "StoreOrder", id: "LIST" }],
    }),
    getMyStoreOrders: builder.query({
      query: (params = {}) => ({ url: "/api/store/orders/my", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "StoreOrder", id })),
              { type: "StoreOrder", id: "MY_LIST" },
            ]
          : [{ type: "StoreOrder", id: "MY_LIST" }],
    }),
    getStoreOrderById: builder.query({
      query: (id) => `/api/store/orders/${id}`,
      providesTags: (result, error, id) => [{ type: "StoreOrder", id }],
    }),
    updateOrderStatus: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/store/orders/${id}/status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StoreOrder", id },
        { type: "StoreOrder", id: "LIST" },
      ],
    }),
    submitPaymentProof: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/store/orders/${id}/payment`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StoreOrder", id },
        { type: "StoreOrder", id: "MY_LIST" },
      ],
    }),
    verifyPayment: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/store/orders/${id}/payment/verify`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StoreOrder", id },
        { type: "StoreOrder", id: "LIST" },
      ],
    }),
    cancelOrder: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/store/orders/${id}/cancel`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StoreOrder", id },
        { type: "StoreOrder", id: "LIST" },
        { type: "StoreOrder", id: "MY_LIST" },
        { type: "StoreProduct", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetStoreSettingsQuery,
  useUpdateStoreSettingsMutation,
  useGetStoreCategoriesQuery,
  useCreateStoreCategoryMutation,
  useUpdateStoreCategoryMutation,
  useDeleteStoreCategoryMutation,
  useGetStoreProductsQuery,
  useGetFeaturedStoreProductsQuery,
  useGetStoreProductByIdQuery,
  useCreateStoreProductMutation,
  useUpdateStoreProductMutation,
  useDeleteStoreProductMutation,
  useGetInventoryTransactionsQuery,
  useGetSizeInventoryQuery,
  useAdjustInventoryMutation,
  usePlaceOrderMutation,
  useGetStoreOrdersQuery,
  useGetMyStoreOrdersQuery,
  useGetStoreOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useSubmitPaymentProofMutation,
  useVerifyPaymentMutation,
  useCancelOrderMutation,
} = storeApi;
